import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  collection,
  onSnapshot,
  query,
  where,
  updateDoc,
  doc,
  arrayRemove,
  arrayUnion,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import {
  Plus,
  Briefcase,
  Rocket,
  AlertTriangle,
  Code2,
  Search,
  Heart,
  MessageCircle,
  MoreVertical,
  Bot,
  CheckCircle2,
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AI_MENTOR_GROUPS } from "../components/chat/mentorCatalog";

const POST_TYPE_ICON = {
  progress: Rocket,
  blocker: AlertTriangle,
  "development-tips": Code2,
  "job-experience": Briefcase,
  "job-referral": Briefcase,
  "looking-for-job": Search,
  "project-showcase": Code2,
};

const POST_TYPE_LABEL = {
  progress: "Progress",
  blocker: "Blocker",
  "development-tips": "Dev Tips",
  "job-experience": "Job Experience",
  "job-referral": "Job Referral",
  "looking-for-job": "Looking for Job",
  "project-showcase": "Project Showcase",
};

const Page = styled.main`
  min-height: 100vh;
  padding: 108px 20px 80px;
  color: #e5e7eb;
  background:
    radial-gradient(820px at 14% 8%, rgba(245, 158, 11, 0.12), transparent 60%),
    radial-gradient(860px at 84% 12%, rgba(56, 189, 248, 0.14), transparent 62%),
    radial-gradient(900px at 50% 100%, rgba(34, 197, 94, 0.06), transparent 68%),
    #020617;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const Section = styled.section`
  margin-top: 0;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 760px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 28px;
  font-weight: 1000;
`;

const SectionSub = styled.p`
  margin: 8px 0 0;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.65;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const CreateBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-radius: 16px;
  font-weight: 900;
  text-decoration: none;
  color: #04111f;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  box-shadow: 0 16px 36px rgba(245, 158, 11, 0.26);
`;

const MentorBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-radius: 16px;
  font-weight: 800;
  border: 1px solid rgba(148, 163, 184, 0.16);
  color: #f8fafc;
  background: rgba(15, 23, 42, 0.48);
  cursor: pointer;
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const FilterChip = styled.button`
  padding: 10px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(245, 158, 11, 0.42)" : "rgba(148, 163, 184, 0.18)"};
  background: ${({ active }) =>
    active
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.16), rgba(56, 189, 248, 0.1))"
      : "rgba(15, 23, 42, 0.48)"};
  color: ${({ active }) => (active ? "#f8fafc" : "#94a3b8")};
  transition: all 0.2s ease;
`;

const Feed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled(motion.div)`
  padding: 22px;
  border-radius: 24px;
  border: 1px solid
    ${({ highlight }) =>
      highlight ? "rgba(245, 158, 11, 0.28)" : "rgba(148, 163, 184, 0.12)"};
  background: ${({ highlight }) =>
    highlight
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(56, 189, 248, 0.05))"
      : "rgba(15, 23, 42, 0.56)"};
  backdrop-filter: blur(10px);
  box-shadow: 0 20px 60px rgba(2, 6, 23, 0.32);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const HeaderRight = styled.div`
  margin-left: auto;
  position: relative;
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 14px;
  color: #04111f;
  flex-shrink: 0;
`;

const AuthorMeta = styled.div`
  min-width: 0;
`;

const AuthorName = styled.div`
  color: #f8fafc;
  font-size: 15px;
  font-weight: 900;
`;

const AuthorSub = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #94a3b8;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #e2e8f0;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(15, 23, 42, 0.48);
`;

const Content = styled.p`
  margin: 16px 0 10px;
  line-height: 1.75;
  font-size: 15px;
  color: #dbe4ef;
`;

const Verified = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 900;
  color: #86efac;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Reflection = styled.p`
  margin-top: 10px;
  font-size: 14px;
  line-height: 1.7;
  color: #cbd5e1;
`;

const Footer = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
`;

const FooterBtn = styled.button`
  background: transparent;
  border: none;
  color: ${({ $active }) => ($active ? "#f8fafc" : "#94a3b8")};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  padding: 0;
`;

const ActionCount = styled.span`
  color: ${({ $active }) => ($active ? "#fde68a" : "#94a3b8")};
  font-weight: 800;
`;

const CommentsWrap = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
  display: grid;
  gap: 12px;
`;

const CommentItem = styled.div`
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.1);
`;

const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  color: #94a3b8;
  font-size: 12px;
`;

const CommentAuthor = styled.div`
  color: #f8fafc;
  font-weight: 800;
`;

const CommentText = styled.div`
  margin-top: 8px;
  color: #dbe4ef;
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-wrap;
`;

const CommentComposer = styled.div`
  display: grid;
  gap: 10px;
`;

const CommentInput = styled.textarea`
  width: 100%;
  min-height: 92px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(2, 6, 23, 0.58);
  color: #e5e7eb;
  padding: 12px 14px;
  outline: none;
  resize: vertical;
  line-height: 1.7;

  &::placeholder {
    color: #64748b;
  }
`;

const CommentActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const CommentHint = styled.div`
  color: #64748b;
  font-size: 12px;
`;

const CommentSendBtn = styled.button`
  padding: 10px 14px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
  color: #04111f;
  background: linear-gradient(135deg, #f59e0b, #facc15);
`;

const MenuBtn = styled.button`
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 6px;
  border-radius: 10px;
`;

const Menu = styled.div`
  position: absolute;
  top: 34px;
  right: 0;
  min-width: 168px;
  background: rgba(15, 23, 42, 0.96);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 14px;
  overflow: hidden;
  z-index: 10;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 10px 14px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 14px;
  color: ${({ danger }) => (danger ? "#fca5a5" : "#e5e7eb")};
  cursor: pointer;
`;

const SkeletonCard = styled.div`
  height: 148px;
  border-radius: 24px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.12) 37%,
    rgba(255, 255, 255, 0.04) 63%
  );
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }
`;

const EmptyState = styled.div`
  padding: 36px 24px;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(15, 23, 42, 0.48);
  text-align: center;
  color: #94a3b8;
  line-height: 1.7;
`;

const JobBox = styled.div`
  margin-top: 14px;
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(34, 197, 94, 0.16),
    rgba(56, 189, 248, 0.06)
  );
  border: 1px solid rgba(34, 197, 94, 0.28);
`;

const JobTitle = styled.div`
  font-size: 15px;
  font-weight: 900;
  color: #f8fafc;
`;

const JobMeta = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const JobRow = styled.div`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #dbe4ef;

  span {
    opacity: 0.72;
    font-weight: 700;
  }

  strong {
    font-weight: 800;
  }

  a {
    color: #7dd3fc;
    font-weight: 700;
    text-decoration: none;
  }
`;

const OpenToWork = styled.div`
  margin-top: 12px;
  font-size: 12px;
  font-weight: 900;
  color: #86efac;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.26);
  padding: 6px 12px;
  border-radius: 999px;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.78);
  backdrop-filter: blur(10px);
  z-index: 50;
  overflow-y: auto;
  padding: 24px;
`;

const Modal = styled.div`
  max-width: 980px;
  margin: 40px auto;
  padding: 28px;
  background:
    linear-gradient(180deg, rgba(10, 16, 28, 0.98), rgba(4, 8, 18, 0.98)),
    rgba(2, 6, 23, 0.96);
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 34px 100px rgba(2, 6, 23, 0.6);
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 28px;
  font-weight: 1000;
`;

const ModalSub = styled.p`
  margin: 10px 0 0;
  color: #94a3b8;
  line-height: 1.7;
`;

const MentorSection = styled.div`
  margin-top: 26px;
`;

const MentorSectionTitle = styled.h3`
  margin: 0;
  color: #f8fafc;
  font-size: 18px;
  font-weight: 900;
`;

const MentorSectionSub = styled.p`
  margin: 6px 0 14px;
  font-size: 14px;
  color: #94a3b8;
`;

const TeacherGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const TeacherCard = styled.div`
  aspect-ratio: 1 / 1;
  border-radius: 22px;
  padding: 18px;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.14);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
`;

const TeacherAvatar = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 18px;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 900;
  color: #04111f;
`;

const TeacherName = styled.div`
  margin-top: 12px;
  font-size: 15px;
  font-weight: 900;
  color: #f8fafc;
`;

const TeacherDesc = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #94a3b8;
  line-height: 1.5;
`;

const StartChatBtn = styled.button`
  margin-top: auto;
  padding: 9px 16px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  color: #04111f;
`;

const FILTERS = [
  { label: "All", value: "all" },
  { label: "Looking for Jobs", value: "looking-for-job" },
  { label: "Development Tips", value: "development-tips" },
  { label: "Job Referrals", value: "job-referral" },
  { label: "Progress", value: "progress" },
];

export default function CommunityFeed() {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [openComments, setOpenComments] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({});

  useEffect(() => {
    const q = query(
      collection(db, "community_posts"),
      where("isDeleted", "==", false)
    );

    const unsub = onSnapshot(q, (snap) => {
      setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return unsub;
  }, []);

  const filteredPosts = useMemo(
    () =>
      posts.filter((post) =>
        activeFilter === "all" ? true : post.postType === activeFilter
      ),
    [posts, activeFilter]
  );

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await updateDoc(doc(db, "community_posts", id), {
      isDeleted: true,
      deletedAt: new Date(),
    });
  };

  const toggleLike = async (post) => {
    if (!user?.uid) return;
    const liked = (post.likes || []).includes(user.uid);
    await updateDoc(doc(db, "community_posts", post.id), {
      likes: liked ? arrayRemove(user.uid) : arrayUnion(user.uid),
    });
  };

  const addComment = async (post) => {
    if (!user?.uid) return;
    const text = (commentDrafts[post.id] || "").trim();
    if (!text) return;

    await updateDoc(doc(db, "community_posts", post.id), {
      comments: arrayUnion({
        id: `${user.uid}-${Date.now()}`,
        uid: user.uid,
        userName: user.displayName || user.email || "User",
        text,
        createdAt: new Date().toISOString(),
      }),
    });

    setCommentDrafts((prev) => ({ ...prev, [post.id]: "" }));
    setOpenComments((prev) => ({ ...prev, [post.id]: true }));
  };

  return (
    <Page>
      <Container>
        <Section>
          <TopBar>
            <div>
              <SectionTitle>Community Feed</SectionTitle>
              <SectionSub>
                Progress, blockers, job signals, and learning proof from people
                following real goals.
              </SectionSub>
            </div>

            <Actions>
              <MentorBtn onClick={() => navigate("/ai-mentors")}>
                <Bot size={17} />
                AI Mentors
              </MentorBtn>
              <CreateBtn href="/community/feeds/create">
                <Plus size={18} />
                Create Post
              </CreateBtn>
            </Actions>
          </TopBar>

          <FiltersBar>
            {FILTERS.map((f) => (
              <FilterChip
                key={f.value}
                active={activeFilter === f.value}
                onClick={() => setActiveFilter(f.value)}
              >
                {f.label}
              </FilterChip>
            ))}
          </FiltersBar>

          <Feed>
            {loading && [1, 2, 3].map((i) => <SkeletonCard key={i} />)}

            {!loading && filteredPosts.length === 0 && (
              <EmptyState>
                No posts match this filter yet.
                <br />
                Try another category or create the first signal for this space.
              </EmptyState>
            )}

            {!loading &&
              filteredPosts.map((post) => {
                const Icon = POST_TYPE_ICON[post.postType] || Code2;
                const likesCount = post.likes?.length || 0;
                const commentsCount = post.comments?.length || 0;
                const likedByMe = !!user?.uid && (post.likes || []).includes(user.uid);
                const commentsOpen = !!openComments[post.id];
                const isOwnPost = user?.uid === post.uid;

                return (
                  <Card
                    key={post.id}
                    highlight={post.postType === "progress"}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <CardHeader>
                      <HeaderLeft>
                        <Avatar>{post.userName?.[0]?.toUpperCase() || "U"}</Avatar>
                        <AuthorMeta>
                          <AuthorName>{post.userName}</AuthorName>
                          <AuthorSub>
                            {post.postType === "progress"
                              ? "Shared verified roadmap progress"
                              : "Shared a community update"}
                          </AuthorSub>
                        </AuthorMeta>
                      </HeaderLeft>

                      <Badge>
                        <Icon size={14} />
                        {POST_TYPE_LABEL[post.postType] || post.postType}
                      </Badge>

                      <HeaderRight>
                        <MenuBtn
                          onClick={() =>
                            setOpenMenuId(openMenuId === post.id ? null : post.id)
                          }
                        >
                          <MoreVertical size={16} />
                        </MenuBtn>

                        {openMenuId === post.id && (
                          <Menu>
                            {isOwnPost && (
                              <>
                                {post.postType !== "progress" && (
                                  <MenuItem
                                    onClick={() => navigate(`/edit-post/${post.id}`)}
                                  >
                                    Edit
                                  </MenuItem>
                                )}
                                <MenuItem danger onClick={() => deletePost(post.id)}>
                                  Delete
                                </MenuItem>
                              </>
                            )}

                            {!isOwnPost && (
                              <>
                                <MenuItem
                                  onClick={() => navigate(`/profile/${post.uid}`)}
                                >
                                  View Profile
                                </MenuItem>

                                {post.postType !== "progress" &&
                                  post.postType !== "looking-for-job" && (
                                    <MenuItem danger>Report</MenuItem>
                                  )}
                              </>
                            )}
                          </Menu>
                        )}
                      </HeaderRight>
                    </CardHeader>

                    {post.postType === "progress" ? (
                      <Content as="div">
                        <strong>
                          Day {post.roadmapRef?.dayNumber} completed
                        </strong>
                        <div style={{ marginTop: 8 }}>{post.roadmapRef?.dayTitle}</div>
                        <Verified>
                          <CheckCircle2 size={14} />
                          Verified from roadmap
                        </Verified>
                        {post.reflection && (
                          <Reflection>Reflection: {post.reflection}</Reflection>
                        )}
                      </Content>
                    ) : post.postType === "looking-for-job" ? (
                      <>
                        <Content>{post.content}</Content>

                        <JobBox>
                          <JobTitle>Open to opportunities</JobTitle>

                          <JobMeta>
                            {post.extra?.role && (
                              <JobRow>
                                <span>Target Role:</span>
                                <strong>{post.extra.role}</strong>
                              </JobRow>
                            )}

                            {post.extra?.experience && (
                              <JobRow>
                                <span>Experience:</span>
                                <strong>{post.extra.experience}</strong>
                              </JobRow>
                            )}

                            {post.extra?.proof && (
                              <JobRow>
                                <span>Portfolio / GitHub:</span>
                                <a
                                  href={post.extra.proof}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View Proof
                                </a>
                              </JobRow>
                            )}
                          </JobMeta>

                          <OpenToWork>Actively looking for a role</OpenToWork>
                        </JobBox>
                      </>
                    ) : (
                      <Content>{post.content}</Content>
                    )}

                    <Footer>
                      <FooterBtn
                        $active={likedByMe}
                        onClick={() => toggleLike(post)}
                      >
                        <Heart size={16} fill={likedByMe ? "currentColor" : "none"} />
                        Like
                        <ActionCount $active={likedByMe}>{likesCount}</ActionCount>
                      </FooterBtn>
                      <FooterBtn
                        $active={commentsOpen}
                        onClick={() =>
                          setOpenComments((prev) => ({
                            ...prev,
                            [post.id]: !prev[post.id],
                          }))
                        }
                      >
                        <MessageCircle size={16} /> Comment
                        <ActionCount $active={commentsOpen}>{commentsCount}</ActionCount>
                      </FooterBtn>
                    </Footer>

                    {commentsOpen && (
                      <CommentsWrap>
                        {(post.comments || []).length ? (
                          (post.comments || []).map((comment) => (
                            <CommentItem key={comment.id}>
                              <CommentMeta>
                                <CommentAuthor>{comment.userName || "User"}</CommentAuthor>
                                <span>
                                  {comment.createdAt
                                    ? new Date(comment.createdAt).toLocaleString()
                                    : "Just now"}
                                </span>
                              </CommentMeta>
                              <CommentText>{comment.text}</CommentText>
                            </CommentItem>
                          ))
                        ) : (
                          <EmptyState style={{ padding: "18px 16px" }}>
                            No comments yet. Start the conversation.
                          </EmptyState>
                        )}

                        <CommentComposer>
                          <CommentInput
                            value={commentDrafts[post.id] || ""}
                            onChange={(event) =>
                              setCommentDrafts((prev) => ({
                                ...prev,
                                [post.id]: event.target.value,
                              }))
                            }
                            placeholder="Write a helpful comment"
                          />
                          <CommentActions>
                            <CommentHint>
                              Keep comments constructive and useful for the community.
                            </CommentHint>
                            <CommentSendBtn onClick={() => addComment(post)}>
                              Post Comment
                            </CommentSendBtn>
                          </CommentActions>
                        </CommentComposer>
                      </CommentsWrap>
                    )}
                  </Card>
                );
              })}
          </Feed>
        </Section>
      </Container>
    </Page>
  );
}
